import { InviteStatus, IProject, IQuery } from '~/client/graph'
import { DeliveriesByProjectIdDocument } from '~/client/graph/operations/generated/Deliveries.generated'
import mobileRoutes, {
  shouldRedirect,
} from '~/client/src/mobile/constants/mobileRoutes'
import MobileEventStore from '~/client/src/mobile/stores/EventStore/MobileEvents.store'
import MobileRootStore from '~/client/src/mobile/stores/MobileRoot.store'
import {
  getJHPRedirectUrl,
  isDeliveryInfoPageRequested,
  isSignUpPageRequested,
} from '~/client/src/shared/constants/commonRoutes'
import Project from '~/client/src/shared/models/Project'
import { GET_DELIVERY_TREE } from '~/client/src/shared/stores/EventStore/EffectsProcessors/WorkerProcessor/workerConstants'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import {
  handleGetDeliveryTicket,
  handleGetDeliveryTicketResult,
  shouldPromptForPassword,
} from '~/client/src/shared/stores/EventStore/handlers'
import getIntegrationsConfigName from '~/client/src/shared/utils/getIntegrationsConfigName'
import { NOOP } from '~/client/src/shared/utils/noop'

import registerMobileChangeEvents from './mobileChangeEvents'

export default function (
  eventsStore: MobileEventStore,
  rootStore: MobileRootStore,
) {
  eventsStore.on(e.INIT_APP_SELECT_PROJECT, () => {
    return {
      dispatch: [e.DISPLAY_PROJECT_PICKER],
    }
  })

  eventsStore.on(
    e.PROJECT_TYPE_OPTIONS_RECEIVED,
    (state, { projectTypeOptions }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS, false)

      state.projectTypeOptions =
        projectTypeOptions.data[0] || state.getDefaultProjectTypeOptions()

      return {
        dispatchN: [[e.LISTEN_TO_PROJECT_TYPE_OPTIONS]],
      }
    },
  )

  // home page could not be opened by default without info on logistics config unlike any other page
  eventsStore.on(e.GET_LOGISTICS_CONFIG_AND_OPEN_DEFAULT_PAGE, state => {
    state.loading.set(e.GET_LOGISTICS_CONFIG_AND_OPEN_DEFAULT_PAGE, true)
    const shouldForceRedirect =
      rootStore.common.history.location.pathname === mobileRoutes.PROJECTS

    return {
      flow: {
        startWith: [[e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS]],
        rules: [
          {
            when: 'on',
            event: e.LOGISTICS_CONFIGURATIONS_RECEIVED,
            dispatch: [e.DISPLAY_DEFAULT_MOBILE_PAGE, shouldForceRedirect],
          },
        ],
      },
    }
  })

  /**
   * Complete initialization if a project was located
   */
  eventsStore.on(e.INIT_APP_3, state => {
    state.loading.set(e.INIT_APP_2, false)
    const {
      activeProject,
      projectTypeOptions: { isDeliveriesDisabled },
    } = state

    if (!activeProject.id) {
      return
    }

    state.filters.currentDate = rootStore.projectDateStore.startOfDay(
      new Date(),
    )
    state.delivery.compactView.activeDate =
      rootStore.projectDateStore.startOfDay(new Date())

    rootStore.clearStoresData()

    const dispatchN: any = [
      [e.GET_PROJECT_CONFIG],
      [
        e.GET_CONFIGURATION,
        getIntegrationsConfigName(state.activeProject.id),
        e.PROJECT_INTEGRATIONS_CONFIGS_RECEIVED,
      ],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS],
      [e.LISTEN_TO_PROJECT],
      [e.LOAD_AND_LISTEN_TO_PROJECT_COLORING_OPTIONS],
      [e.GET_LOGISTICS_CONFIG_AND_OPEN_DEFAULT_PAGE],
      [e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_ZONEMAP_THRESHOLDS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY],
      [e.LOAD_AND_LISTEN_TO_FORM_CATEGORIES],
      [e.LOAD_AND_LISTEN_TO_PERMIT_TYPES],
      [e.REGISTER_SERVICE_WORKER],
      [e.GET_PROJECT_MEMBERS],
      [e.LOAD_AND_LISTEN_TO_OPERATION_RULES],
      [e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_ROLES],
      [e.LOAD_AND_LISTEN_TO_PROJECT_TRADES],
      [e.LOAD_LOCATIONS_AND_LISTEN_CHANGES],
      [e.LISTEN_TO_WORKFLOW_LOCATION_RESTRICTION],
      [e.LOAD_RESOURCE_SCHEMAS],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTIONS],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES],
      [e.LOAD_AND_LISTEN_TO_COMPANIES],
      [e.LOAD_AND_LISTEN_TO_USER_PROJECTS],
      [e.LOAD_AND_LISTEN_TO_SITEMAPS],
      [e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS],
      [e.LISTEN_TO_GLOBE_VIEW_ITEMS_DATA],
      [e.LOAD_AND_LISTEN_TO_BASEMAPS],
      [e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS],
      // Markups load lazily per view; on (re)init just reset + start the subscription.
      [e.CLEAR_MARKUPS],
      [e.LISTEN_TO_MARKUPS],
      [e.LOAD_AND_LISTEN_TO_MESSAGES],
      [e.LOAD_AND_LISTEN_TO_PHOTOS],
      [e.LOAD_AND_LISTEN_TO_SCANNERS],
      [e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS],
      [e.LOAD_CLOSURES_AND_LISTEN_CHANGES],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION],
      [e.LISTEN_TO_USERS],
      [e.UPDATE_USER_PREFERENCES],
      [e.LOAD_AND_LISTEN_TO_NOTIFICATIONS_COUNT],
      [e.LOAD_AND_LISTEN_TO_NOTIFICATIONS],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES],
      [e.LOAD_AND_LISTEN_TO_MATERIALS],
      [e.GET_SHARED_SITE_PERMITS],
      [e.LISTEN_TO_SHARED_SITE_PERMITS],
    ]

    if (!isDeliveriesDisabled) {
      dispatchN.push(
        [e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS],
        [e.LISTEN_AND_LOAD_DELIVERIES],
        [e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS],
        [e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS],
        [e.LISTEN_TO_RECURRING_DELIVERIES_SETTING],
        [e.LOAD_AND_LISTEN_TO_ALL_DELIVERY_ATTRIBUTES],
        [e.GET_SHARED_DELIVERIES],
        [e.LISTEN_TO_SHARED_DELIVERIES],
      )
    } else {
      rootStore.deliveriesStore.isDataReceived = true
    }

    if (!state.isTilesetsDisabled) {
      dispatchN.push(
        [e.LOAD_AND_LISTEN_TO_TILESETS],
        [e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS],
      )
    }

    if (activeProject && state.userActiveProjectSettings) {
      if (state.userActiveProjectSettings.isPending) {
        state.userActiveProjectSettings.setInviteStatus(InviteStatus.Accepted)
        dispatchN.push([
          e.SAVE_USER_PROJECTS,
          [state.userActiveProjectSettings.toDto()],
        ])
      }
    } else {
      state.didInitialize = false
    }

    // STX1-1057: a first-time user whose domain declares no SSO rule has a
    // password as their only possible credential. Prompt for one now — once
    // their 3-day invite key expires, an un-prompted user has no way back in.
    if (shouldPromptForPassword(rootStore)) {
      dispatchN.push([e.DISPLAY_SAVE_PASSWORD])
    }

    return {
      dispatchN,
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_NOTIFICATIONS_COUNT, () => {
    return {
      dispatch: [e.LISTEN_TO_NOTIFICATIONS_COUNT],
    }
  })

  eventsStore.on(
    e.CALCULATE_DELIVERY_TREE,
    (state, deliveryTreeData, onSuccess) => {
      state.loading.set(e.CALCULATE_DELIVERY_TREE, true)

      return {
        worker: {
          type: GET_DELIVERY_TREE,
          data: JSON.stringify(deliveryTreeData),
          onSuccess: [e.CALCULATE_DELIVERY_TREE_SUCCESS, onSuccess],
          onError: [e.COMPLETE_REQUEST, e.CALCULATE_DELIVERY_TREE],
        },
      }
    },
  )

  eventsStore.on(
    e.CALCULATE_DELIVERY_TREE_SUCCESS,
    (state, onSuccess, tree) => {
      if (onSuccess) {
        onSuccess(tree)
      }

      state.loading.set(e.CALCULATE_DELIVERY_TREE, false)
    },
  )

  eventsStore.on(e.DISPLAY_PROJECT_PICKER, (state, forceRedirect) => {
    const { pathname } = location

    state.loading.set(e.INIT_APP_2, false)
    if (forceRedirect || shouldRedirect(pathname, mobileRoutes.PROJECTS)) {
      // in case history has not been initialized yet, load this route when its innited
      state.initialRoute = mobileRoutes.PROJECTS
      // if it has in fact been inited, lets just go there
      rootStore.common._displayView(state.initialRoute)
    }
  })

  eventsStore.on(e.ACTIVATE_PROJECT_SUCCESS, (state, project: IProject) => {
    state.activeProject = Project.fromDto(project)
    eventsStore.dispatch(e.INVALIDATE_LOCATION_LOAD)
    state.resetResourceDisplayedOnGlobalCard()
    rootStore.clearStoresData()

    eventsStore.terminateGraphSubscriptions()

    return {
      flow: {
        // Navigate to default mobile page with the selected project, forcing
        // the redirect even when on /projects. The DISPLAY_DEFAULT_MOBILE_PAGE
        // handler gates on shouldRedirect(pathname, defaultRoute), which
        // returns false when pathname === /projects and the target is
        // /project/<code>/home (mobileRoutes.ts:48-100 lists PROJECTS in the
        // not-redirect-from fallback). Without the force flag the URL stays
        // on /projects through ACTIVATE_PROJECT_SUCCESS and only flips later
        // when LOGISTICS_CONFIGURATIONS_RECEIVED triggers a second forced
        // dispatch — producing a visible spinner → picker → spinner flicker.
        // HANDLE_SAME_PROJECT_SELECTION already passes `true` here for the
        // same reason (mobileChangeEvents.ts:75).
        startWith: [
          [e.DISPLAY_DEFAULT_MOBILE_PAGE, true],
          [e.RESET_INPUT_STATUSES],
          [e.GET_AUTH_USER_PROJECT],
        ],
        rules: [
          {
            when: 'on',
            event: e.GET_AUTH_USER_PROJECT_SUCCESS,
            dispatch: [e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS],
          },
          {
            when: 'on',
            event: e.PROJECT_TYPE_OPTIONS_RECEIVED,
            dispatch: [e.INIT_APP_3],
            shouldTerminate: true,
          },
        ],
      },
    }
  })

  eventsStore.on(e.DISPLAY_DEFAULT_MOBILE_PAGE, (_state, forceRedirect) => {
    const { history, viewToDisplay: defaultRoute } = rootStore.common
    const { pathname } = history.location

    if (forceRedirect || shouldRedirect(pathname, defaultRoute)) {
      rootStore.common._displayView(defaultRoute)
    }
  })

  eventsStore.on(
    e.GET_PROJECT_MEMBERS_SUCCESS,
    (state, { users, deletedUsers }) => {
      state.loading.set(e.GET_PROJECT_MEMBERS, false)
      rootStore.projectMembersStore.receiveList(users.data)
      rootStore.projectMembersStore.receiveDeletedList(deletedUsers)
    },
  )

  eventsStore.on(e.GET_DELIVERY_TICKET, handleGetDeliveryTicket)

  eventsStore.on(e.GET_DELIVERY_TICKET_RESULT, handleGetDeliveryTicketResult)

  eventsStore.on(e.INIT_PROJECT_INTEGRATIONS, () => {
    rootStore.concreteDirectIntegrationStore.init()
  })

  eventsStore.on(e.TRY_UNAUTHORIZED_MODE, state => {
    if (
      isDeliveryInfoPageRequested() ||
      isSignUpPageRequested() ||
      location.pathname.includes(mobileRoutes.SEND_SMS_DELIVERY_TICKET)
    ) {
      state.isUnauthorizedMode = true
    }

    const jhpUrl = getJHPRedirectUrl(state.initPathname, state.initProjectCode)
    if (jhpUrl) {
      window.location.href = jhpUrl
      return
    }

    return { dispatch: [e.TRY_UNAUTHORIZED_MODE_RESULT] }
  })

  eventsStore.on(e.GO_TO_AVAILABLE_PROJECT, () => {
    rootStore.common.displayProjects()
  })

  eventsStore.on(e.LOAD_DELIVERIES, state => {
    state.loading.set(e.LOAD_DELIVERIES, true)

    const { addMonths, startOfDay } = rootStore.projectDateStore
    const baseDate = startOfDay(Date.now())

    const fromDate = addMonths(baseDate, -1).getTime()
    const toDate = addMonths(baseDate, 1).getTime()

    return {
      graphQuery: {
        query: DeliveriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
          fromDate,
          toDate,
          limit: 1000,
        },
        onSuccess: [e.LOAD_DELIVERIES_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOAD_DELIVERIES],
      },
    }
  })

  // service event
  // see
  eventsStore.on(e.SHOW_ONE_TIME_POPUP, NOOP)

  registerMobileChangeEvents(eventsStore, rootStore)
}
